import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Send, ArrowLeft, Shield, Star, MapPin, Building2, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PIOChat() {
  const { assignmentId } = useParams();
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [chatData, setChatData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchChat = useCallback(async () => {
    try {
      const res = await fetch(`${API}/pio-chat/${assignmentId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setChatData(data);
        setMessages(data.messages || []);
      }
    } catch (e) { console.error(e); }
  }, [API, assignmentId]);

  useEffect(() => { fetchChat(); }, [fetchChat]);

  // Poll for new messages every 15s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/pio-chat/${assignmentId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch (e) { /* silent */ }
    }, 15000);
    return () => clearInterval(interval);
  }, [API, assignmentId]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, {
      message_id: `temp_${Date.now()}`,
      sender: "owner",
      sender_name: user?.name || "Owner",
      content: userMsg,
      created_at: new Date().toISOString()
    }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/pio-chat/${assignmentId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: userMsg }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          const filtered = prev.filter(m => !m.message_id.startsWith("temp_"));
          return [...filtered, data.owner_message, data.pio_response];
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const pio = chatData?.pio;
  const property = chatData?.property;
  const assignment = chatData?.assignment;

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = "";
  messages.forEach((msg) => {
    const date = formatDate(msg.created_at);
    if (date !== lastDate) {
      groupedMessages.push({ type: "date", date });
      lastDate = date;
    }
    groupedMessages.push({ type: "message", ...msg });
  });

  if (!chatData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="pio-chat-page" className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="bg-[#11130F] border border-white/10 rounded-sm p-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-pio-btn"
            onClick={() => navigate("/dashboard/pio")}
            className="text-white/40 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
            <span className="text-[#D4AF37] font-serif text-lg">{pio?.name?.charAt(0) || "P"}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-base text-white truncate">{pio?.name || "PIO"}</h3>
              <span className="w-2 h-2 bg-[#556B2F] rounded-full" title="Online" />
            </div>
            <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-sans">{pio?.specialization}</p>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-white/30">
            <div className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              <span className="text-[10px] font-sans">{property?.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span className="text-[10px] font-sans capitalize">{assignment?.scope?.replace(/_/g, " ")}</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[10px] font-sans text-[#D4AF37]">{pio?.rating}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 bg-[#0C0D0B] border border-white/10 rounded-sm overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h3 className="font-serif text-lg text-white mb-2">Start a conversation</h3>
            <p className="text-white/40 text-sm font-sans max-w-sm mb-6">
              {pio?.name} is your assigned Property Intelligence Officer for {property?.name}. Ask questions, request updates, or discuss property matters.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md">
              {[
                `What's the current status of ${property?.name || "my property"}?`,
                "Any risks or red flags identified?",
                "When is the next scheduled inspection?",
                "Can you provide a progress update?"
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="text-left bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-white/50 text-xs font-sans hover:border-[#D4AF37]/40 hover:text-white/70 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {groupedMessages.map((item, i) => {
          if (item.type === "date") {
            return (
              <div key={`date-${i}`} className="flex items-center justify-center py-2">
                <span className="bg-white/5 text-white/30 text-[10px] font-sans px-3 py-1 rounded-full">{item.date}</span>
              </div>
            );
          }

          const isOwner = item.sender === "owner";
          return (
            <div key={item.message_id} className={`flex gap-2.5 ${isOwner ? "justify-end" : "justify-start"}`}>
              {!isOwner && (
                <div className="w-7 h-7 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center shrink-0 mt-1">
                  <span className="text-[#D4AF37] font-serif text-xs">{pio?.name?.charAt(0) || "P"}</span>
                </div>
              )}
              <div className={`max-w-[75%] ${isOwner ? "items-end" : "items-start"}`}>
                <div className={`rounded-sm px-4 py-3 ${
                  isOwner
                    ? "bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                    : "bg-[#11130F] border border-white/10"
                }`}>
                  {!isOwner && (
                    <p className="text-[#D4AF37] text-[10px] font-sans font-medium mb-1">{item.sender_name}</p>
                  )}
                  <p className="text-white/80 text-sm font-sans whitespace-pre-wrap leading-relaxed">{item.content}</p>
                </div>
                <p className={`text-white/20 text-[10px] font-sans mt-1 ${isOwner ? "text-right" : ""}`}>
                  {formatTime(item.created_at)}
                </p>
              </div>
              {isOwner && (
                <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center shrink-0 mt-1">
                  <User className="w-3.5 h-3.5 text-white/60" />
                </div>
              )}
            </div>
          );
        })}

        {sending && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center shrink-0">
              <span className="text-[#D4AF37] font-serif text-xs">{pio?.name?.charAt(0) || "P"}</span>
            </div>
            <div className="bg-[#11130F] border border-white/10 rounded-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 text-[#D4AF37] animate-spin" />
                <span className="text-white/40 text-xs font-sans">{pio?.name} is typing...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <input
          data-testid="pio-chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${pio?.name || "PIO"}...`}
          className="flex-1 bg-[#11130F] border border-white/10 rounded-sm px-4 py-3 text-white text-sm font-sans placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
          disabled={assignment?.status !== "active"}
        />
        <Button
          data-testid="pio-chat-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || sending || assignment?.status !== "active"}
          className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm px-4 disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
      {assignment?.status !== "active" && (
        <p className="text-white/30 text-xs font-sans mt-2 text-center">This assignment has been completed. Chat is read-only.</p>
      )}
    </div>
  );
}
